import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useCooldownAction } from 'hooks/useCooldownAction';
import apiUtils from 'utils/apiutils';
import { toast } from 'sonner';
import {
  mainstrategy,
  slpriceoptions,
  tpSlOptions,
  liquidationOptions,
  timeframeOptions,
  guidebasedonstrategy
} from 'constants/constant';
import DateRangePicker from 'components/ui/DateRangePicker';
import { FaBook } from 'react-icons/fa';
import { Tooltip } from 'antd';

const strategyConfigMap = {
  'TP_SL': {
    apiEndpoint: '/backtest/tp-sl-backtest',
    requiredFields: ['strategy_name', 'exchange', 'symbol', 'start_date', 'end_date', 'timeframe', 'leverage', 'entry_price', 'entry_condition', 'fee_rate'],
    dynamicFields: {
      long: ['investment_long', 'take_profit_long', 'stop_loss_long'],
      short: ['investment_short', 'take_profit_short', 'stop_loss_short'],
      hedge: ['investment_long', 'investment_short', 'take_profit_long', 'take_profit_short', 'stop_loss_long', 'stop_loss_short', 'new_sl']
    }
  },
  'Liquidation': {
    apiEndpoint: '/backtest/liquidation-backtest',
    requiredFields: [
      'exchange',
      'symbol',
      // 'trade_direction',
      'start_date',
      'end_date',
      'timeframe',
      'entry_price',
      'entry_condition',
      'leverage',
      'initial_investment',
      'subsequent_multiplier',
      'take_profit_percentage',
      'tp_type',
      'stop_loss_percent',
      'fee_rate'
    ],
    dynamicFields: {}
  }
};

const BacktestConfiguration = ({ onBacktestComplete, setIsRunning, setresultloaded }) => {
  const [selectedMainStrategy, setSelectedMainStrategy] = useState('TP_SL');
  const [exchangeOptions, setExchangeOptions] = useState([]);
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const runWithCooldown = useCooldownAction();
  // const [backtestResults, setBacktestResults] = useState([]);
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);


  // Fetch exchanges dynamically
  const fetchExchanges = async (strategy) => {
    try {
      const payload = {
        Strategy_Named: strategy
      }

      const response = await apiUtils.post(`/backtest/exchange-list`, payload, { showToast: false });

      setExchangeOptions(response.Success?.exchange_list);
      setConfig((prev) => ({ ...prev, exchange: null })); // reset selection
    } catch (err) {
      console.error("Error fetching exchanges:", err);
    }
  };

  // Fetch symbols dynamically
  const fetchSymbols = async (strategy, exchange) => {
    try {
      const payload = {
        Strategy_Named: strategy,
        Exchange_Name: exchange
      }
      const response = await apiUtils.post(`/backtest/symbol-list`, payload, { showToast: false });
      setSymbolOptions(response.Success?.symbol_list || []);
      setConfig((prev) => ({ ...prev, symbol: null }));
    } catch (err) {
      console.error("Error fetching symbols:", err);
    }
  };

  const [config, setConfig] = useState({
    strategy_name: '',
    exchange: '',
    symbol: '',
    start_date: oneMonthAgo.toISOString().split("T")[0],
    end_date: today.toISOString().split("T")[0],
    timeframe: '',
    leverage: 1,
    entry_price: 10,
    entry_condition: 'Greater Than Equal To',
    fee_rate: 0.0005,
    investment_long: 100,
    investment_short: 100,
    take_profit_long: 10,
    take_profit_short: 10,
    stop_loss_long: 5,
    stop_loss_short: 5,
    new_sl: '',

    initial_investment: 1,
    subsequent_multiplier: 2,
    take_profit_percentage: 1,
    tp_type: 'Pair_Price',
    stop_loss_percent: 1,
    strategy_type: ''
  });

  useEffect(() => {
    setConfig(prev => ({
      ...prev,
      strategy_type: selectedMainStrategy
    }));
  }, [selectedMainStrategy]);

  // Strategy-specific options
  const currentStrategyOptions = selectedMainStrategy === 'TP_SL'
    ? tpSlOptions
    : selectedMainStrategy === 'Liquidation'
      ? liquidationOptions
      : [];

  const handleInputChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleMainStrategySelect = (val) => {
    setSelectedMainStrategy(val);
    handleInputChange('strategy_name', '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setresultloaded(true)
    if (!selectedMainStrategy) {
      toast.error("Please select a main strategy first.");
      return;
    }

    const strategyConfig = strategyConfigMap[selectedMainStrategy];
    if (!strategyConfig) return;

    // Collect required fields
    let requiredFields = [...strategyConfig.requiredFields];

    // Add TP_SL dynamic fields
    if (selectedMainStrategy === 'TP_SL') {
      const strategyName = config.strategy_name;
      if (strategyName.includes('Long')) requiredFields.push(...strategyConfig.dynamicFields.long);
      if (strategyName.includes('Short')) requiredFields.push(...strategyConfig.dynamicFields.short);
      if (strategyName.includes('Hedge')) requiredFields.push(...strategyConfig.dynamicFields.hedge);
    }

    // Validate fields
    const newErrors = {};
    requiredFields.forEach(field => {
      if (config[field] === '' || config[field] === null || config[field] === undefined) {
        newErrors[field] = 'This field is required';
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(`Please fill the following fields: ${Object.keys(newErrors).map(f => f.replace(/_/g, ' ')).join(', ')}`);
      return;
    }

    // API call
    await runWithCooldown(async () => {
      setIsRunning(true);
      try {
        const response = await apiUtils.post(strategyConfig.apiEndpoint, {
          ...config,
          trade_direction: config.strategy_name === "Long" ? "Long" : "Short",
        }, { showToast: false });
        if (response?.Success) {
          // setBacktestResults(response.Success);
          onBacktestComplete({ strategy_type: selectedMainStrategy, data: response.Success });
          toast.success("Backtest completed successfully!");
        }
      } catch (err) {
        toast.error(err.response?.data?.Error);
      } finally {
        setIsRunning(false);
      }
    });
  };

  useEffect(() => {
    fetchExchanges(selectedMainStrategy);
    setConfig(prev => ({ ...prev, exchange: '', symbol: '' }));
  }, [])

  useEffect(() => {
    if (exchangeOptions.length > 0 && config.exchange) {
      fetchSymbols(selectedMainStrategy, config.exchange);
    }
  }, [config.exchange])

  const isLongStrategy = config.strategy_name.includes('Long') || config.strategy_name.includes('Hedge');
  const isShortStrategy = config.strategy_name.includes('Short') || config.strategy_name.includes('Hedge');
  const isHedgeStrategy = config.strategy_name.includes('Hedge');




  return (
    <div className="bg-card border border-border rounded-xl p-3 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Settings" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Backtest Configuration</h2>
            <p className="text-sm text-muted-foreground">Configure your strategy testing parameters</p>
          </div>
        </div>
      </div>

      <div className='grid grid-cols-1  lg:grid-cols-8 gap-2'>

        <form onSubmit={handleSubmit} className="space-y-8 col-span-6 ">

          {/* Common Inputs: Date Range + Main Strategy */}
          <section className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-3">
            <div className="space-y-6 px-3 py-6 md:p-6 bg-background rounded-2xl shadow-lg border border-border">
              {/* Date Range */}
              <div className="bg-muted/50  rounded-lg">
                <h3 className="font-semibold md:flex items-center text-foreground mb-3">
                  <Tooltip className='border-b-2 border-dotted border-white ' title={selectedMainStrategy === "TP_SL" ? "Select the start and end dates for backtesting. Maximum range is 60 days." : "Select the start and end dates for backtesting. Maximum range is 3 years."}>
                    <div className='flex items-center'>
                      <Icon name="Calendar" size={18} className="mr-2 text-primary " />
                      Date Range
                    </div>
                  </Tooltip>
                  <span className="text-sm text-muted-foreground ml-2">
                    (
                    {selectedMainStrategy === "TP_SL"
                      ? "Max 60 days for Backtesting"
                      : selectedMainStrategy === "Liquidation"
                        ? "Max 3 years for Backtesting"
                        : "Select a strategy to view limits"}
                    )
                  </span>

                </h3>
                <DateRangePicker
                  condition={selectedMainStrategy === "TP_SL"}
                  value={{ start_date: config.start_date, end_date: config.end_date }}
                  onChange={(range) => {
                    handleInputChange("start_date", range.start_date);
                    handleInputChange("end_date", range.end_date);
                  }}
                  error={errors.start_date || errors.end_date}
                />
              </div>

              {/* Main Strategy */}
              <div className="bg-muted/50  rounded-lg">
                <h3 className="font-semibold flex items-center text-foreground mb-3">
                  <Icon name="Bot" size={18} className="mr-2 text-primary" />
                  Main Strategy Selection
                </h3>

                <Select
                  label="Main Strategy"
                  tooltip="Choose the main trading strategy you want to test."
                  options={mainstrategy}
                  value={selectedMainStrategy}
                  onChange={handleMainStrategySelect}
                  className="my-4"
                />
                {selectedMainStrategy && (
                  <Select
                    label="Strategy"
                    tooltip="Select a sub-strategy under the main strategy (example: Long, Short, Hedge)."
                    options={currentStrategyOptions}
                    value={config.strategy_name}
                    onChange={(val) => handleInputChange('strategy_name', val)}
                  />
                )}
              </div>
            </div>

            {/* Market Setup */}
            <div className="space-y-6 p-6 bg-background rounded-2xl shadow-lg border border-border">
              <h3 className="font-semibold flex items-center text-foreground mb-3 col-span-full lg:col-span-1">
                <Icon name="TrendingUp" size={18} className="mr-2 text-primary" />
                Market Setup
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select
                  label="Exchange"
                  tooltip="Choose the exchange where you want to run backtesting."
                  options={exchangeOptions.map(ex => ({ value: ex, label: ex }))}
                  value={config.exchange}
                  onChange={(val) => handleInputChange("exchange", val)}
                  error={errors.exchange}
                />

                <Select
                  label="Timeframe"
                  tooltip="Select the candle time interval (like 1h, 6h, 1D)."
                  options={
                    selectedMainStrategy === "Liquidation"
                      ? timeframeOptions.filter((opt) => opt.value === "1d")
                      : timeframeOptions
                  }
                  value={config.timeframe}
                  onChange={(val) => handleInputChange("timeframe", val)}
                  error={errors.timeframe}
                />
                <Select
                  label="Symbol"
                  tooltip="Choose the trading pair for backtesting (example: BTCUSDT)."
                  options={symbolOptions.map(sym => {
                    const base = sym.replace("USDT", "");
                    const formatted = `${base}/USDT`;   // DOGE/USDT

                    return {
                      value: formatted,   // PASS DOGE/USDT
                      label: formatted    // SHOW DOGE/USDT
                    };
                  })}
                  value={config.symbol}
                  onChange={(val) => handleInputChange('symbol', val)}
                  error={errors.symbol}
                  searchable
                />
              </div>


              <h3 className="font-semibold flex items-center text-foreground mb-3 col-span-full lg:col-span-1">
                <Icon name="LogIn" size={18} className="mr-2 text-primary" />
                Entry Setup
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="Leverage"
                  tooltip="Select the leverage multiplier (example: 1x, 2x, 5x)."
                  type="number"
                  min={1}
                  max={10}
                  value={config.leverage}
                  onChange={(e) => {
                    let val = parseInt(e.target.value, 10);
                    if (val < 1) val = 1;
                    if (val > 10) val = 10;
                    handleInputChange('leverage', val);
                  }}
                  error={errors.leverage}
                />
                {/* <Input
                  label="Fee Rate"
                  tooltip="Trading fee rate per trade (e.g., 0.0005 for 0.05%)"
                  type="number"
                  step="0.0001"
                  value={config.fee_rate}
                  onChange={(e) => handleInputChange('fee_rate', e.target.value)}
                  error={errors.fee_rate}
                /> */}
              </div>
              {isHedgeStrategy && (
                <div className=" bg-background rounded-2xl shadow-lg ">
                  {/* <h3 className="font-semibold flex items-center text-foreground mb-3">
                      <Icon name="Activity" size={18} className="mr-2 text-blue-600" />
                      Hedge Settings
                    </h3> */}
                  <Select
                    label="SL Price"
                    tooltip="Stop Loss rule for the opposite trade after one trade hits Take Profit."
                    options={slpriceoptions}
                    value={config.new_sl}
                    onChange={(val) => handleInputChange('new_sl', val)}
                    error={errors.new_sl}
                  />
                </div>
              )}
            </div>
            {/* TP_SL Fields */}
            {selectedMainStrategy === 'TP_SL' && config.strategy_name && (
              <>
                {/* <section className="grid grid-cols-1 gap-6"> */}
                {isLongStrategy && (
                  <div className="p-6 bg-background rounded-2xl shadow-lg border border-border">
                    <Tooltip className='border-b-2 border-dotted border-white ' title="Set all parameters for Long trades.">
                      <h3 style={{ display: "ruby-text" }} className="font-semibold  items-center text-foreground mb-3">
                        <Icon name="ArrowUpRight" size={18} className="mr-2 text-green-600" />
                        Long Settings
                      </h3>
                    </Tooltip>
                    <div className="space-y-3">
                      <Input
                        label="Investment Long"
                        tooltip={"Amount you want to invest in the Long position."}
                        type="number"
                        value={config.investment_long}
                        onChange={(e) => handleInputChange('investment_long', e.target.value)}
                        error={errors.investment_long}
                      />
                      <Input
                        label="Take Profit Long"
                        tooltip="Profit target for the Long trade (in %)."
                        type="number"
                        value={config.take_profit_long}
                        onChange={(e) => handleInputChange('take_profit_long', e.target.value)}
                        error={errors.take_profit_long}
                      />
                      <Input
                        label="Stop Loss Long"
                        tooltip="Loss limit for the Long trade (in %). "
                        type="number"
                        value={config.stop_loss_long}
                        onChange={(e) => handleInputChange('stop_loss_long', e.target.value)}
                        error={errors.stop_loss_long}
                      />
                    </div>
                  </div>
                )}
                {isShortStrategy && (
                  <div className="p-6 bg-background rounded-2xl shadow-lg border border-border">
                    <Tooltip className='border-b-2  border-dotted border-white ' title="Set all parameters for the Short trade.">
                      <h3 style={{ display: "ruby-text" }} className="font-semibold   w-full max-w-full items-center text-foreground mb-3">
                        <Icon name="ArrowDownRight" size={18} className="mr-2 text-red-600" />
                        Short Settings
                      </h3>
                    </Tooltip>
                    <div className="space-y-3">
                      <Input
                        label="Investment Short"
                        tooltip="Amount to invest in the Short position."
                        type="number"
                        value={config.investment_short}
                        onChange={(e) => handleInputChange('investment_short', e.target.value)}
                        error={errors.investment_short}
                      />
                      <Input
                        label="Take Profit Short"
                        tooltip="Profit target for the Short trade (in %)."
                        type="number"
                        value={config.take_profit_short}
                        onChange={(e) => handleInputChange('take_profit_short', e.target.value)}
                        error={errors.take_profit_short}
                      />
                      <Input
                        label="Stop Loss Short"
                        tooltip="Loss limit for the Short trade (in %)."
                        type="number"
                        value={config.stop_loss_short}
                        onChange={(e) => handleInputChange('stop_loss_short', e.target.value)}
                        error={errors.stop_loss_short}
                      />
                    </div>
                  </div>
                )}
                {/* </section> */}
              </>
            )}
            {/* Liquidation Fields */}
            {selectedMainStrategy === 'Liquidation' && config.strategy_name && (
              <section className="grid grid-cols-1 gap-6">
                <div className="p-6 bg-background rounded-2xl shadow-lg border border-border">
                  <Tooltip className='border-b-2  border-dotted border-white ' title={config.strategy_name === 'Long' ? "Set all parameters for Long side liquidation strategy." : "Set all parameters for Short side liquidation strategy."}>
                    <h3 style={{ display: "ruby-text" }} className="font-semibold   w-full max-w-full items-center text-foreground mb-3">
                      <Icon name="Activity" size={18} className="mr-2 text-yellow-600" />
                      Liquidation Settings
                    </h3>
                  </Tooltip>
                  <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-3">
                    {/* <Select
                      label="Trade Direction"
                      tooltip="Select trade direction for liquidation strategy"
                      options={[{ label: 'Long', value: 'Long' }, { label: 'Short', value: 'Short' }]}
                      value={config.trade_direction}
                      onChange={(val) => handleInputChange('trade_direction', val)}
                      error={errors.trade_direction}
                    /> */}
                    <Input
                      label="Initial Investment"
                      tooltip={config.strategy_name === 'Long' ? "Amount used for the first Long trade." : "Amount used for the first Short trade."}
                      type="number"
                      value={config.initial_investment}
                      onChange={(e) => handleInputChange('initial_investment', e.target.value)}
                      error={errors.initial_investment}
                    />
                    <Input
                      label="Subsequent Multiplier"
                      tooltip="Multiplier for next trade after a loss.
Example: If set to 2x and initial is 100, next trade becomes 200, then 400, etc."
                      type="number"
                      value={config.subsequent_multiplier}
                      onChange={(e) => handleInputChange('subsequent_multiplier', e.target.value)}
                      error={errors.subsequent_multiplier}
                    />
                    <Input
                      label="Take Profit %"
                      tooltip="Profit target in %.
(100% = breakeven, 200% = double your investment)"
                      type="number"
                      value={config.take_profit_percentage}
                      onChange={(e) => handleInputChange('take_profit_percentage', e.target.value)}
                      error={errors.take_profit_percentage}
                    />
                    <Select
                      label="TP Type"
                      tooltip={config.strategy_name === 'Long' ? <div style={{ lineHeight: 1.6 }}>
                        <strong>How Take Profit is calculated:</strong>
                        <br />
                        <strong>Pair Price</strong> — TP based on coin price
                        <br />
                        <small>Example: buy at 1 USDT, TP 200% → sell at 3 USDT</small>
                        <br />
                        <strong>Fund</strong> — TP based on total invested amount
                        <br />
                        <small>Example: invest $100, TP 200% → sell when fund = $300</small>
                      </div> : <div style={{ lineHeight: 1.6 }}>
                        <strong>How Take Profit is calculated:</strong>
                        <br />
                        <strong>Pair Price</strong> — TP based on coin price
                        <br />
                        <small>Example: Short at 1 USDT, TP 200% → close at 0.5 USDT</small>
                        <br />
                        <strong>Fund</strong> — TP based on total invested amount
                        <br />
                        <small>Example: invest $100, TP 200% → close when fund becomes $200</small>
                      </div>}
                      options={[{ label: 'Pair_Price', value: 'Pair_Price' }, { label: 'Fund', value: 'Fund' }]}
                      value={config.tp_type}
                      onChange={(val) => handleInputChange('tp_type', val)}
                      error={errors.tp_type}
                    />
                    <Input
                      label="Stop Loss %"
                      tooltip="Loss limit in %.
(100% = liquidation price)"
                      type="number"
                      value={config.stop_loss_percent}
                      onChange={(e) => handleInputChange('stop_loss_percent', e.target.value)}
                      error={errors.stop_loss_percent}
                    />
                  </div>
                </div>
              </section>
            )}
          </section>

          <div className="flex flex-col sm:flex-row  flex-wrap items-center justify-end gap-3 pt-4 border-t border-border">
            <Button type="submit" iconName="Play" iconPosition="left" className="min-w-40 bg-primary rounded-xl hover:bg-primary/90">
              Run Backtest
            </Button>
          </div>
        </form>

        <div className="lg:col-span-2 mt-5 md:mt-0 p-5 rounded-xl border border-border">
          <h3 className="text-lg flex items-center gap-2 font-semibold mb-3 text-foreground">
            <span className='text-primary'><FaBook /></span>  Guide of Strategy
          </h3>

          <div
            className="prose prose-sm max-w-none text-foreground h-80 overflow-y-auto pr-2
    scrollbar-thin
    scrollbar-thumb-white/70
    scrollbar-track-transparent
    hover:scrollbar-thumb-white
    [&::-webkit-scrollbar]:w-1
    [&::-webkit-scrollbar-thumb]:bg-primary
    [&::-webkit-scrollbar-thumb]:rounded-full
    [&::-webkit-scrollbar-track]:bg-transparent
    [&::-webkit-scrollbar-thumb]:transition-colors
    [&::-webkit-scrollbar-thumb]:duration-300
    hover:[&::-webkit-scrollbar-thumb]:bg-white
  "
            dangerouslySetInnerHTML={{
              __html:
                guidebasedonstrategy.find((g) => g.value === selectedMainStrategy)
                  ?.content ||
                "<p class='text-muted-foreground'>Select a strategy to view detailed guide.</p>",
            }}
          />
        </div>



      </div>
    </div>
  );
};

export default BacktestConfiguration;
